/**
 * Public pages for an agency (no sign-in):
 *   /tip/<CODE>   send an anonymous tip
 *   /news/<CODE>  the newsroom: media releases the sheriff's office has released to the public
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../shared/api.ts';
import { BlueMark } from '../shared/BlueMark.tsx';
import { ErrorBox, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';

function Top({ name, sub }: { name: string | null | undefined; sub: string }) {
  return (
    <header className="xfer-top">
      <BlueMark size={34} />
      <div>
        <strong>{name ?? 'Sheriff’s Office'}</strong>
        <div className="small">{sub}</div>
      </div>
    </header>
  );
}

export function TipPage({ code }: { code: string }) {
  const r = useLoad(() => api.get<any>(`/api/public/tips/${encodeURIComponent(code)}`), [code]);
  const a = useAction();
  const [f, setF] = useState({ category: '', details: '', location: '', happened: '', about: '', vehicle: '', contactName: '', contactPhone: '', contactEmail: '', website: '' });
  const [contact, setContact] = useState(false);
  const [done, setDone] = useState<{ number: string; followCode: string } | null>(null);
  useEffect(() => {
    if (r.data?.agencyName) document.title = `Send a tip · ${r.data.agencyName}`;
  }, [r.data]);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      const body = contact ? f : { ...f, contactName: '', contactPhone: '', contactEmail: '' };
      setDone(await api.post(`/api/public/tips/${encodeURIComponent(code)}`, body));
      window.scrollTo(0, 0);
    });
  };
  return (
    <div className="xfer-page tip-page">
      <Top name={r.data?.agencyName} sub="Anonymous tip line" />
      <main className="xfer-main stack">
        {r.error ? (
          <ErrorBox error={r.error} />
        ) : !r.data ? (
          <Spinner />
        ) : done ? (
          <div className="card tip-done stack">
            <h1>Thank you. Your tip was sent.</h1>
            <p>Write these down if you want to give us more information later. They’re the only way we can match it to your tip.</p>
            <dl className="tip-codes">
              <div>
                <dt>Tip number</dt>
                <dd>{done.number}</dd>
              </div>
              <div>
                <dt>Follow-up code</dt>
                <dd>{done.followCode}</dd>
              </div>
            </dl>
            <button className="btn" onClick={() => (setDone(null), setF({ category: '', details: '', location: '', happened: '', about: '', vehicle: '', contactName: '', contactPhone: '', contactEmail: '', website: '' }))}>
              Send another tip
            </button>
          </div>
        ) : (
          <form className="card stack tip-form" onSubmit={submit}>
            <h1>Send an anonymous tip</h1>
            <Notice tone="bad">
              <strong>Emergency? Call 911.</strong> This form isn’t watched around the clock.
            </Notice>
            <p className="small muted">You don’t have to give your name. We don’t record your IP address or device with your tip.</p>
            <ErrorBox error={a.error} />
            <label className="field">
              <span className="field-l">What is it about?</span>
              <select value={f.category} onChange={set('category')}>
                <option value="">Choose one (optional)</option>
                {r.data.categories.map((c: string) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field-l">What do you know? *</span>
              <textarea rows={6} required minLength={10} maxLength={8000} value={f.details} onChange={set('details')} placeholder="What happened or is happening, as much detail as you can." />
            </label>
            <label className="field">
              <span className="field-l">Where?</span>
              <input value={f.location} onChange={set('location')} maxLength={300} placeholder="Address, business, road or landmark" />
            </label>
            <label className="field">
              <span className="field-l">When?</span>
              <input value={f.happened} onChange={set('happened')} maxLength={200} placeholder="Date and time, or “every night after 10”" />
            </label>
            <label className="field">
              <span className="field-l">Who is involved?</span>
              <input value={f.about} onChange={set('about')} maxLength={300} placeholder="Names, nicknames, descriptions" />
            </label>
            <label className="field">
              <span className="field-l">Vehicle</span>
              <input value={f.vehicle} onChange={set('vehicle')} maxLength={300} placeholder="Color, make, model, tag" />
            </label>
            <label className="tip-hp" aria-hidden="true">
              Website
              <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set('website')} />
            </label>
            <label className="check">
              <input type="checkbox" checked={contact} onChange={(e) => setContact(e.target.checked)} /> I’m willing to be contacted (optional)
            </label>
            {contact && (
              <div className="grid2">
                <label className="field">
                  <span className="field-l">Name</span>
                  <input value={f.contactName} onChange={set('contactName')} maxLength={200} />
                </label>
                <label className="field">
                  <span className="field-l">Phone</span>
                  <input value={f.contactPhone} onChange={set('contactPhone')} maxLength={40} inputMode="tel" />
                </label>
                <label className="field">
                  <span className="field-l">Email</span>
                  <input type="email" value={f.contactEmail} onChange={set('contactEmail')} maxLength={200} />
                </label>
              </div>
            )}
            <div>
              <button className="btn primary" disabled={a.busy}>
                {a.busy ? 'Sending…' : 'Send tip'}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

export function NewsPage({ code }: { code: string }) {
  const r = useLoad(() => api.get<any>(`/api/public/news/${encodeURIComponent(code)}`), [code]);
  useEffect(() => {
    if (r.data?.agencyName) document.title = `News · ${r.data.agencyName}`;
  }, [r.data]);
  return (
    <div className="xfer-page">
      <Top name={r.data?.agencyName} sub="News releases" />
      <main className="xfer-main stack">
        {r.error ? (
          <ErrorBox error={r.error} />
        ) : !r.data ? (
          <Spinner />
        ) : !r.data.releases.length ? (
          <p className="muted">No news releases yet.</p>
        ) : (
          r.data.releases.map((x: any) => (
            <article key={x.number} className="card news-item">
              <div className="small muted">
                {new Date(x.released_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} · {x.number}
              </div>
              <h2>{x.title}</h2>
              <div className="news-body">{x.body}</div>
            </article>
          ))
        )}
      </main>
    </div>
  );
}
